import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Hint } from "@/components/ui/hint";
import { Input } from "@/components/ui/input";
import { CirclePlus, Trash2 } from "lucide-react";
import { useFieldArray, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface VariantsListProps {
  name: string;
  translationKeys: {
    variant: string;
    addVariant: string;
    noVariantsFound: string;
  };
}

export default function VariantsList({
  name,
  translationKeys,
}: VariantsListProps) {
  const { t } = useTranslation();

  const { isSubmitting, disabled } = useFormState();
  const { fields, append, remove } = useFieldArray({ name });

  const onAddVariant = () => {
    append({ variant: "" });
  };

  const onRemoveVariant = (index: number) => {
    remove(index);
  };

  return (
    <Card className="bg-white p-3">
      <CardContent className="p-2">
        {fields.length === 0 && (
          <Hint>{t(translationKeys.noVariantsFound)}</Hint>
        )}
        {fields.map((field, variantIndex) => {
          return (
            <FormField
              key={variantIndex}
              name={`${name}.${variantIndex}.variant`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(translationKeys.variant, {
                      number: variantIndex + 1,
                    })}
                  </FormLabel>
                  <div className="flex flex-row max-w-96">
                    <FormControl className="w-full">
                      <Input className="w-full" {...field} />
                    </FormControl>
                    <Button
                      type="button"
                      onClick={() => onRemoveVariant(variantIndex)}
                      size="icon"
                      variant="ghost"
                      className="ml-2"
                      disabled={field.disabled}
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        })}
      </CardContent>
      <CardFooter className="p-2">
        <Button
          type="button"
          onClick={onAddVariant}
          disabled={isSubmitting || disabled}
          size="sm"
          variant="secondary"
        >
          <CirclePlus className="w-4 h-4 mr-3" />
          {t(translationKeys.addVariant)}
        </Button>
      </CardFooter>
    </Card>
  );
}
